<template>
  <div class="print-page">
    <div class="print-toolbar no-print">
      <v-btn to="/iabf/admin/carpools" small>&lt; Back to Carpool Admin</v-btn>
      <v-btn small color="primary" class="ml-2" @click="doPrint">🖨 Print</v-btn>
      <label class="filter-label ml-3">
        <input type="checkbox" v-model="hideEmpty" /> Hide empty carpools
      </label>
      <span class="ml-3 muted">{{ visibleSlots.length }} of {{ regularSlots.length }} carpools</span>
    </div>

    <h1>IABF {{ adminFestivalYear }} Carpool Driver Sheets</h1>
    <p class="subtitle">One carpool per block. Includes each rider's phone, flight time, flight number, and on/offsite location.</p>

    <p v-if="loading" class="muted">Loading…</p>

    <section
      v-for="slot in visibleSlots"
      :key="slot.id"
      class="slot-card"
    >
      <header class="slot-header">
        <div class="slot-title">
          <span class="dir-badge" :class="'dir-' + slot.direction">{{ slot.direction === 'arrival' ? 'ARRIVAL · pickup at airport' : 'DEPARTURE · drop at airport' }}</span>
          <strong class="slot-time">{{ slot.date }} · {{ slot.time }}</strong>
          <span class="slot-airport">{{ airportLabel(slot.airport) }}</span>
        </div>
        <div class="slot-meta">
          {{ (slot.dancers || []).length }} / {{ slot.maxSeats }} riders
          <button
            v-if="groupPhones(slot).length"
            type="button"
            class="copy-btn no-print"
            :title="`Includes Julie (${formatPhone(ADMIN_PHONE)}) so she's auto-added to the group thread`"
            @click="copyGroupPhones(slot)"
          >
            {{ copiedGroupId === slot.id ? '✓ Copied' : `📋 Copy ${groupPhones(slot).length} + me` }}
          </button>
          <button
            type="button"
            class="copy-btn copy-btn-secondary no-print"
            @click="copyWelcomeMessage(slot)"
          >
            {{ copiedWelcomeId === slot.id ? '✓ Copied' : '✉ Copy welcome message' }}
          </button>
        </div>
      </header>

      <table v-if="(slot.dancers || []).length" class="riders-table">
        <thead>
          <tr>
            <th>Rider</th>
            <th>Phone</th>
            <th>{{ slot.direction === 'arrival' ? 'Flight arrival' : 'Flight departure' }}</th>
            <th>Flight #</th>
            <th>{{ slot.direction === 'arrival' ? 'Drop-off' : 'Pick-up' }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="d in slot.dancers" :key="d.id">
            <td class="name">
              {{ d.name }}
              <span v-if="isGuestRider(d)" class="guest-tag no-print">faculty</span>
            </td>
            <td class="phone-cell">
              {{ formatPhone(riderInfo(d).phone) }}
              <button
                v-if="riderInfo(d).phone"
                type="button"
                class="copy-icon no-print"
                :title="copiedPhoneKey === slot.id + ':' + d.id ? 'Copied' : 'Copy phone number'"
                @click="copyOnePhone(slot, d)"
              >{{ copiedPhoneKey === slot.id + ':' + d.id ? '✓' : '📋' }}</button>
            </td>
            <td>{{ formatTime12(slot.direction === 'arrival' ? riderInfo(d).arrivalTime : riderInfo(d).departureTime) }}</td>
            <td>{{ slot.direction === 'arrival' ? riderInfo(d).arrivalFlightNumber : riderInfo(d).departureFlightNumber }}</td>
            <td>{{ locationLabel(slot.direction === 'arrival' ? d.dropoff : d.pickup) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted">No riders.</p>
    </section>

    <p v-if="!regularSlots.length && !loading" class="muted">No carpools loaded.</p>
  </div>
</template>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { carpoolDateSortKey } from '@/config/iabf-festival-dates'

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      hideEmpty: true,
      loading: true,
      copiedGroupId: null,
      copiedPhoneKey: null,
      copiedWelcomeId: null,
      // Julie's number — auto-included on group-phone copies so whoever
      // starts the group thread (usually Jen) doesn't have to add her.
      ADMIN_PHONE: '18588371062'
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    await Promise.all([
      this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true }),
      this.$store.dispatch('loadIABFDancers'),
      this.$store.dispatch('loadAllTravelDetails')
    ])
    this.loading = false
  },
  computed: {
    slots () {
      return this.$store.getters.iabfCarpoolSlots || []
    },
    dancers () {
      return this.$store.getters.loadedIABFDancers || []
    },
    travelDetails () {
      return this.$store.getters.loadedAllTravelDetails || []
    },
    travelByPersonId () {
      const map = new Map()
      for (const t of this.travelDetails) {
        if (t.personId) map.set(t.personId, t)
      }
      return map
    },
    dancerById () {
      const map = new Map()
      for (const d of this.dancers) map.set(d.id, d)
      return map
    },
    regularSlots () {
      return this.slots
        .filter(s => !s.isWaitlist)
        .slice()
        .sort((a, b) => {
          // Sort by date, then by time of day. Arrival/departure are kept
          // interleaved so a single printed run is in time order.
          const da = this.dateSortKey(a.date)
          const db = this.dateSortKey(b.date)
          if (da !== db) return da - db
          const ta = this.parseTime(a.time)
          const tb = this.parseTime(b.time)
          return (ta === null ? Infinity : ta) - (tb === null ? Infinity : tb)
        })
    },
    visibleSlots () {
      if (!this.hideEmpty) return this.regularSlots
      return this.regularSlots.filter(s => (s.dancers || []).length > 0)
    }
  },
  methods: {
    doPrint () {
      window.print()
    },
    riderInfo (rider) {
      // Riders inside a slot reference the dancer by id. Pull phone +
      // flight data by joining against the dancers and travel details
      // collections. For guests (faculty added directly to a slot) we read
      // the phone and flight time off the slot entry itself, since they
      // have no dancer doc.
      const dancer = this.dancerById.get(rider.id) || {}
      const travel = this.travelByPersonId.get(rider.id) || {}
      return {
        phone: dancer.phone || rider.phone || '',
        arrivalTime: travel.arrivalTime || rider.guestFlightTime || '',
        arrivalFlightNumber: travel.arrivalFlightNumber || '',
        departureTime: travel.departureTime || rider.guestFlightTime || '',
        departureFlightNumber: travel.departureFlightNumber || ''
      }
    },
    isGuestRider (rider) {
      return !!rider.isGuest || String(rider.id || '').startsWith('guest-') || !this.dancerById.has(rider.id)
    },
    async saveFlightTime (slot, rider, raw) {
      try {
        await this.$store.dispatch('updateCarpoolRiderFlightTime', {
          slotId: slot.id,
          riderId: rider.id,
          flightTime: raw
        })
        await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      } catch (e) {
        console.error('updateCarpoolRiderFlightTime failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save flight time. ' + (e && e.message ? e.message : ''))
      }
    },
    async savePhone (slot, rider, raw) {
      const trimmed = String(raw || '').trim()
      try {
        await this.$store.dispatch('updateCarpoolRiderPhone', {
          slotId: slot.id,
          riderId: rider.id,
          phone: trimmed
        })
        // Refresh slots so the UI shows the saved value.
        await this.$store.dispatch('loadCarpoolSlots', { useAdminYear: true })
      } catch (e) {
        console.error('updateCarpoolRiderPhone failed:', e)
        // eslint-disable-next-line no-alert
        window.alert('Could not save phone. ' + (e && e.message ? e.message : ''))
      }
    },
    groupPhones (slot) {
      return (slot.dancers || [])
        .map(d => this.riderInfo(d).phone)
        .filter(p => !!p)
    },
    async copyToClipboard (text) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch (e) {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand('copy') } catch (err) { /* noop */ }
        document.body.removeChild(ta)
        return true
      }
    },
    async copyOnePhone (slot, dancer) {
      const phone = this.riderInfo(dancer).phone
      if (!phone) return
      await this.copyToClipboard(this.formatPhone(phone))
      this.copiedPhoneKey = slot.id + ':' + dancer.id
      setTimeout(() => {
        if (this.copiedPhoneKey === slot.id + ':' + dancer.id) this.copiedPhoneKey = null
      }, 1500)
    },
    welcomeMessage (slot) {
      const time = this.formatTime12(slot.time)
      const airport = this.airportLabel(slot.airport)
      if (slot.direction === 'arrival') {
        return `Hello IABF-ers! Welcome to the ${time} carpool from ${airport}! Once your flight lands, please let us know in this group. If you discover your flight is delayed, please also let us know and we may shift you into a later car. Once you all meet up at the airport, text the group to let us know you're ready for your Uber. We'll call the Uber and it will pick you up. We'll make 1 stop at the Atlantic Center for the Arts and a second stop at the SpringHill Suites. Can't wait to see you soon!`
      }
      const springTime = time
      const acaTime = this.addMinutesToClockTime(slot.time, 15)
      return `Hello IABF-ers! Welcome to the ${time} carpool to ${airport}! Please let us know in the group if you'd like to be picked up at the Atlantic Center for the Arts or the SpringHill Suites. The pickup will be at ${springTime} at SpringHill Suites and at ${acaTime} at the Atlantic Center for the Arts. If nobody wants to be picked up offsite, the pickup time will be ${springTime} at the Atlantic Center for the Arts.`
    },
    addMinutesToClockTime (timeStr, mins) {
      const s = String(timeStr || '').trim()
      const parsed = this.parseClockTime(s)
      if (parsed === null) return s
      let total = parsed + mins
      total = ((total % 1440) + 1440) % 1440
      const h24 = Math.floor(total / 60)
      const m = total % 60
      let h = h24 % 12
      if (h === 0) h = 12
      const mer = h24 >= 12 ? 'PM' : 'AM'
      return `${h}:${String(m).padStart(2, '0')} ${mer}`
    },
    parseClockTime (str) {
      if (!str) return null
      const s = String(str).toLowerCase().trim()
      const m = s.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/)
      if (!m) return null
      let h = parseInt(m[1], 10)
      const min = parseInt(m[2] || '0', 10)
      const mer = m[3]
      if (mer === 'pm' && h < 12) h += 12
      if (mer === 'am' && h === 12) h = 0
      return h * 60 + min
    },
    async copyWelcomeMessage (slot) {
      const text = this.welcomeMessage(slot)
      await this.copyToClipboard(text)
      this.copiedWelcomeId = slot.id
      setTimeout(() => {
        if (this.copiedWelcomeId === slot.id) this.copiedWelcomeId = null
      }, 1800)
    },
    async copyGroupPhones (slot) {
      const phones = this.groupPhones(slot)
      if (!phones.length) return
      // Auto-include Julie's number so whoever starts the group thread
      // (usually Jen) doesn't have to remember to add her separately.
      // Dedupe by stripped digits so we don't double her up if she's
      // already in the list for some reason.
      const seen = new Set(phones.map(p => String(p).replace(/\D/g, '').replace(/^1/, '')))
      const adminDigits = this.ADMIN_PHONE.replace(/\D/g, '').replace(/^1/, '')
      const all = phones.slice()
      if (!seen.has(adminDigits)) all.push(this.ADMIN_PHONE)
      const joined = all.map(p => this.formatPhone(p)).join(', ')
      await this.copyToClipboard(joined)
      this.copiedGroupId = slot.id
      setTimeout(() => {
        if (this.copiedGroupId === slot.id) this.copiedGroupId = null
      }, 1800)
    },
    formatTime12 (raw) {
      const s = String(raw || '').trim()
      if (!s) return ''
      // If the value already mentions am/pm anywhere, leave it alone.
      if (/am|pm/i.test(s)) return s
      // Match a leading HH:MM (with optional surrounding text). Anything that
      // doesn't look like a clock time (free-text like "Depends on…") falls
      // through untouched.
      const m = s.match(/^(\d{1,2}):(\d{2})$/)
      if (!m) return s
      let h = parseInt(m[1], 10)
      const min = m[2]
      if (!isFinite(h) || h < 0 || h > 23) return s
      const mer = h >= 12 ? 'PM' : 'AM'
      h = h % 12
      if (h === 0) h = 12
      return `${h}:${min} ${mer}`
    },
    formatPhone (raw) {
      const digits = String(raw || '').replace(/\D/g, '')
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      if (digits.length === 11 && digits.startsWith('1')) {
        const d = digits.slice(1)
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
      }
      return raw || ''
    },
    airportLabel (code) {
      const map = {
        MCO: 'Orlando (MCO)',
        DAB: 'Daytona Beach (DAB)',
        SFB: 'Sanford (SFB)'
      }
      return map[code] || code || ''
    },
    locationLabel (val) {
      if (val === 'atlantic') return 'Onsite (ACA)'
      if (val === 'salty_mermaid') return 'Offsite (SpringHill Suites)'
      return val || '—'
    },
    dateSortKey (dateStr) {
      return carpoolDateSortKey(dateStr, this.adminFestivalYear)
    },
    parseTime (timeStr) {
      if (!timeStr) return null
      const s = String(timeStr).toLowerCase().trim()
      const m = s.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/)
      if (!m) return null
      let h = parseInt(m[1], 10)
      const min = parseInt(m[2] || '0', 10)
      const mer = m[3]
      if (mer === 'pm' && h < 12) h += 12
      if (mer === 'am' && h === 12) h = 0
      return h * 60 + min
    }
  }
}
</script>

<style scoped>
.print-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  color: #1a1a1a;
}

.print-toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
}

.filter-label {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.muted {
  color: #999;
  font-style: italic;
}

h1 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 700;
  font-size: 30px;
  margin: 0 0 4px;
}

.subtitle {
  color: #555;
  font-size: 13px;
  margin: 0 0 18px;
}

.slot-card {
  border: 1px solid #e6e1d4;
  border-radius: 6px;
  margin-bottom: 16px;
  page-break-inside: avoid;
}

.slot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  background: #f7f5ef;
  border-bottom: 1px solid #e6e1d4;
  border-radius: 6px 6px 0 0;
}

.slot-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dir-badge {
  font-family: Quattrocento, Georgia, serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
}

.dir-arrival {
  background: #e0f2f1;
  color: #00695c;
}

.dir-departure {
  background: #fff3e0;
  color: #e65100;
}

.slot-time {
  font-family: Quattrocento, Georgia, serif;
  font-size: 15px;
}

.slot-airport {
  font-weight: 700;
  color: #555;
}

.slot-meta {
  font-size: 13px;
  color: #555;
  display: flex;
  align-items: center;
  gap: 10px;
}

.copy-btn {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 12px;
  background: #267E7D;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  letter-spacing: 0.02em;
}

.copy-btn:hover {
  background: #2f9695;
}

.copy-btn-secondary {
  background: #dd4762;
}

.copy-btn-secondary:hover {
  background: #c43a54;
}

.phone-cell {
  white-space: nowrap;
}

.copy-icon {
  background: transparent;
  border: none;
  font-size: 13px;
  cursor: pointer;
  margin-left: 4px;
  padding: 0 2px;
  opacity: 0.6;
}

.copy-icon:hover {
  opacity: 1;
}

.guest-tag {
  display: inline-block;
  margin-left: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: #efebe9;
  color: #5d4037;
  padding: 1px 6px;
  border-radius: 8px;
}

.phone-input {
  width: 140px;
  padding: 2px 6px;
  border: 1px solid #d0cbc4;
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
}

.phone-input:focus {
  border-color: #267E7D;
  outline: none;
}

.phone-print-only,
.flight-print-only {
  display: none;
}

.flight-input {
  width: 110px;
  padding: 2px 6px;
  border: 1px solid #d0cbc4;
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
}

.flight-input:focus {
  border-color: #267E7D;
  outline: none;
}

@media print {
  .phone-print-only,
  .flight-print-only {
    display: inline;
  }
}

.riders-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.riders-table th,
.riders-table td {
  text-align: left;
  padding: 6px 14px;
  border-bottom: 1px solid #f1eee9;
  vertical-align: top;
}

.riders-table th {
  background: #fbfaf6;
  font-family: Quattrocento, Georgia, serif;
  font-size: 12px;
  letter-spacing: 0.04em;
}

.riders-table tr:last-child td {
  border-bottom: none;
}

.riders-table .name {
  font-weight: 700;
}

@media print {
  .no-print {
    display: none !important;
  }
  .print-page {
    padding: 0;
    max-width: none;
  }
  h1 {
    font-size: 22px;
  }
  .slot-card {
    margin-bottom: 10px;
  }
  .riders-table {
    font-size: 11px;
  }
  .riders-table th,
  .riders-table td {
    padding: 3px 8px;
  }
}
</style>
</content>
</invoke>